/* eslint-disable no-console */

import { PersistedSession } from './dataModels/PersistedSession';
import { SessionCookie } from './dataModels/SessionCookie';
import { SsoPortalEligibilityData } from './dataModels/SsoPortalEligibilityData';
import { PORTAL_VALUE_UNKNOWN } from './constants';

export const SSO_SESSION_COOKIE_NAME = 'FINDCARE_AND_COST_SSO_SESSION';
export const SESSION_DURATION_SECS = 60 * 20; // 20 minutes
export const SESSION_TABLE_NAME = 'sso-sessions-multiple';
export const DYNAMO_DB_VERSION = '2012-08-10';
export const SESSION_COOKIE_PROPS = 'Path=/; Secure';
export const CREATE_SESSION_ACTION_TYPE_CREATE = 'create';
export const CREATE_SESSION_ACTION_TYPE_DROP = 'drop';
export const OPTUM_CORRELATION_ID_HEADER = 'optum-cid-ext';

export const isSessionValid = (cookie: SessionCookie): boolean => {
  const { lastAccessedSecs, durationSecs } = cookie;
  if (lastAccessedSecs && durationSecs) {
    return new Date().getTime() < lastAccessedSecs * 1000 + durationSecs * 1000;
  }
  return false;
};

export const getSessionDurationSecs = (env): number => {
  return !isNaN(env.SESSION_DURATION_SECS) ? parseInt(env.SESSION_DURATION_SECS) : SESSION_DURATION_SECS;
};

export const constructSessionValue = (memberId, sessionId, lastAccessedTimeSecs, durationSecs) => {
  return `${memberId}!${sessionId}!${lastAccessedTimeSecs}!${durationSecs}`;
};

export const constructSessionCookie = (sessionValue, durationSecs, domain) => {
  const domainProp = domain ? `Domain=${domain}; ` : '';
  return `${SSO_SESSION_COOKIE_NAME}=${sessionValue}; ${domainProp}${SESSION_COOKIE_PROPS}; Max-Age=${durationSecs}`;
};

export const parseSessionCookie = (sessionValue): SessionCookie => {
  const parts = sessionValue?.split('!');
  return {
    memberId: parts?.[0],
    sessionId: parts?.[1],
    lastAccessedSecs: !isNaN(parts?.[2]) ? parseInt(parts[2]) : 0,
    durationSecs: !isNaN(parts?.[3]) ? parseInt(parts[3]) : 0,
  };
};

export const isSessionExpired = (ttlSec: number, currentTimeSecs) => {
  return ttlSec <= currentTimeSecs;
};

export const createSession = async (ddb, token, updatedTtl, memberId, ssoAttributes) => {
  const serializedSsoAttributes = JSON.stringify(ssoAttributes);
  const putItemParams = {
    TableName: SESSION_TABLE_NAME,
    Item: {
      sessionId: { S: token },
      memberId: { S: memberId },
      timeToLive: { N: updatedTtl.toString() },
      ssoAttributes: { S: serializedSsoAttributes },
    },
  };
  return await ddb.putItem(putItemParams).promise();
};

export const updateSession = async (ddb, token, updatedTtl) => {
  const updateItemParams = {
    TableName: SESSION_TABLE_NAME,
    Key: {
      sessionId: { S: token },
    },
    UpdateExpression: 'SET timeToLive = :ttl', // timeToLive is the colName in DynamoDB, :ttl is the alias
    ExpressionAttributeValues: {
      ':ttl': { N: updatedTtl.toString() },
    },
  };
  return await ddb.updateItem(updateItemParams).promise();
};

export const dropSession = async (ddb, token) => {
  const deleteItemParams = {
    TableName: SESSION_TABLE_NAME,
    Key: {
      sessionId: { S: token },
    },
  };
  return await ddb.deleteItem(deleteItemParams).promise();
};

export const getSession = async (ddb, token): Promise<PersistedSession> => {
  const getItemParams = {
    TableName: SESSION_TABLE_NAME,
    Key: {
      sessionId: { S: token },
    },
  };
  const storedSession = await ddb.getItem(getItemParams).promise();

  const ttlSecsStr = storedSession?.Item?.timeToLive?.N;
  const ttlSecs = !isNaN(ttlSecsStr) ? parseInt(ttlSecsStr) : 0;

  const sessionAttributesStr = storedSession?.Item?.ssoAttributes?.S;
  const ssoAttributes = sessionAttributesStr ? JSON.parse(sessionAttributesStr) : {};
  return {
    sessionId: storedSession?.Item?.sessionId?.S,
    memberId: storedSession?.Item?.memberId?.S,
    ttlSecs,
    ssoAttributes,
  };
};

export const formatDateByYearMonthDay = (inputDate: string) => {
  const dateObj = new Date(inputDate);
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const constructResponse = (callback, statusCode, correlationId, sessionCookie?, data?) => {
  let headers = {};
  let multiValueHeaders = {};

  if (correlationId) {
    headers = { ...headers, [OPTUM_CORRELATION_ID_HEADER]: correlationId };
  }

  // ALB requires Set-Cookie in multiValueHeaders format (array)
  if (sessionCookie) {
    multiValueHeaders = { ...multiValueHeaders, 'Set-Cookie': [sessionCookie] };
  }

  let responseBody = {};
  responseBody = {
    ...responseBody,
    statusCode,
    headers,
  };

  // Include multiValueHeaders if we have cookies to set
  if (sessionCookie) {
    responseBody = { ...responseBody, multiValueHeaders };
  }

  if (data) {
    responseBody = { ...responseBody, body: JSON.stringify(data) };
  }
  return callback(null, responseBody);
};

export const extractCookieDomainFromRequest = (event, env): string => {
  // Handle both API Gateway (headers) and ALB (multiValueHeaders) formats
  let referer = event?.headers?.Referer || event?.headers?.referer;

  // If not in headers, check multiValueHeaders (ALB format)
  if (!referer && event?.multiValueHeaders) {
    const refererArray = event.multiValueHeaders.Referer || event.multiValueHeaders.referer;
    referer = refererArray && refererArray[0];
  }

  console.log(`Referer: ${referer}`);

  if (!env.ALLOWED_DOMAINS) {
    throw new Error('ALLOWED_DOMAINS environment variable is not configured');
  }
  const allowedDomains = env.ALLOWED_DOMAINS.split(',').map((allowedDomain) => allowedDomain.trim());

  // When no Referer (e.g. direct ALB POST), fall back to the Host header
  let hostname: string;
  if (referer) {
    hostname = new URL(referer).hostname || '';
  } else {
    hostname =
      event?.headers?.Host ||
      event?.headers?.host ||
      event?.multiValueHeaders?.Host?.[0] ||
      event?.multiValueHeaders?.host?.[0] ||
      '';
    console.log(`No Referer header found, falling back to Host: ${hostname}`);
  }

  const refererHostname = hostname;

  for (const allowedDomain of allowedDomains) {
    if (refererHostname.includes(allowedDomain)) {
      return `.${allowedDomain}`;
    }
  }
  throw new Error(`Referer domain (${refererHostname}) not found in allowed domains`);
};

export const getPortalSource = (portalEligibilityData: SsoPortalEligibilityData): string => {
  const portalSource = portalEligibilityData?.portalSource;
  if (portalSource) {
    return portalSource;
  }
  return PORTAL_VALUE_UNKNOWN;
};

export const getUserType = (portalEligibilityData: SsoPortalEligibilityData): string => {
  const userType = portalEligibilityData?.callingUser;
  if (userType) {
    return userType;
  }
  return PORTAL_VALUE_UNKNOWN;
};
